import { decodeCookie, getCookie, removeCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import {
    stopWatchingForOtherTabSessionChange,
} from "@insite/client-framework/Services/SessionService";

const SHARED_COOKIE_NAMES = [
    "CurrentBillToId",
    "CurrentShipToId",
    "CurrentPickUpWarehouseId",
    "CurrentFulfillmentMethod",
];

const getCanonicalCookieName = (name: string) => `Shared${name}`;

const getContextCookieOptions = () => {
    const isRememberedUser = !!getCookie("SetRememberedUserId");
    return isRememberedUser ? { expires: 30 } : {};
};

const getVisibleCookieValues = (name: string) =>
    document.cookie
        .split("; ")
        .filter(cookie => cookie.startsWith(`${name}=`))
        .map(cookie => decodeCookie(cookie.substring(name.length + 1)));

let sharedContextWatcherId: NodeJS.Timeout | undefined;

export const getCurrentBillToIdForSharedContext = () =>
    getCookie("SharedCurrentBillToId") || getCookie("CurrentBillToId");

export function stopWatchingForSharedContextChange(): void {
    stopWatchingForOtherTabSessionChange();

    if (sharedContextWatcherId === undefined) {
        return;
    }

    window.clearInterval(sharedContextWatcherId);
    sharedContextWatcherId = undefined;
}

export function watchForSharedContextChange(): void {
    if (typeof window === "undefined" || sharedContextWatcherId !== undefined) {
        return;
    }

    stopWatchingForOtherTabSessionChange();

    const previousBillToId = getCurrentBillToIdForSharedContext();

    sharedContextWatcherId = setInterval(() => {
        if (previousBillToId === getCurrentBillToIdForSharedContext()) {
            return;
        }

        window.location.reload();
        stopWatchingForSharedContextChange();
    }, 1000);
}

export function getSharedCookieDomain(hostname: string): string | undefined {
    if (!hostname || hostname === "localhost") {
        return;
    }
    if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) {
        return;
    }
    const parts = hostname.split(".").filter(Boolean);
    if (parts.length < 2) {
        return;
    }
    return `.${parts.slice(-2).join(".")}`;
}

export function reconcileContextCookiesFromCanonical(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    const cookieDomain = getSharedCookieDomain(window.location.hostname);
    if (!cookieDomain) {
        return false;
    }

    const options = getContextCookieOptions();
    let billToWasStale = false;

    for (const name of SHARED_COOKIE_NAMES) {
        const canonicalName = getCanonicalCookieName(name);
        const canonicalValue = getCookie(canonicalName);
        if (!canonicalValue) {
            continue;
        }

        if (name === "CurrentBillToId") {
            billToWasStale = getVisibleCookieValues(name).some(value => value !== canonicalValue);
        }

        removeCookie(name, { path: "/" });
        removeCookie(name, { path: "/", domain: cookieDomain });
        removeCookie(canonicalName, { path: "/" });
        removeCookie(canonicalName, { path: "/", domain: cookieDomain });
        setCookie(name, canonicalValue, { path: "/", domain: cookieDomain, ...options });
        setCookie(canonicalName, canonicalValue, { path: "/", domain: cookieDomain, ...options });
    }

    return billToWasStale;
}

export function removeSharedContextCookies(): void {
    if (typeof window === "undefined") {
        return;
    }

    const cookieDomain = getSharedCookieDomain(window.location.hostname);

    for (const name of SHARED_COOKIE_NAMES) {
        removeCookie(name, { path: "/" });
        removeCookie(getCanonicalCookieName(name), { path: "/" });
        if (cookieDomain) {
            removeCookie(name, { path: "/", domain: cookieDomain });
            removeCookie(getCanonicalCookieName(name), { path: "/", domain: cookieDomain });
        }
    }
}

export function normalizeSharedContextCookies(): void {
    if (typeof window === "undefined") {
        return;
    }

    const cookieDomain = getSharedCookieDomain(window.location.hostname);
    if (!cookieDomain) {
        return;
    }

    const options = getContextCookieOptions();

    stopWatchingForSharedContextChange();

    for (const name of SHARED_COOKIE_NAMES) {
        const valueBeforeRemovingHostCookie = getCookie(name);

        removeCookie(name, { path: "/" });

        const sharedValue = getCookie(name);
        const canonicalName = getCanonicalCookieName(name);
        const value = getCookie(canonicalName) || sharedValue || valueBeforeRemovingHostCookie;

        removeCookie(name, { path: "/", domain: cookieDomain });
        removeCookie(canonicalName, { path: "/" });
        removeCookie(canonicalName, { path: "/", domain: cookieDomain });

        if (value !== undefined && value !== null && value !== "") {
            setCookie(name, value, { path: "/", domain: cookieDomain, ...options });
            setCookie(canonicalName, value, { path: "/", domain: cookieDomain, ...options });
        }
    }

    watchForSharedContextChange();
}

export function promoteContextCookiesToSharedDomain(): void {
    if (typeof window === "undefined") {
        return;
    }

    const cookieDomain = getSharedCookieDomain(window.location.hostname);
    if (!cookieDomain) {
        return;
    }

    const options = getContextCookieOptions();

    stopWatchingForSharedContextChange();

    for (const name of SHARED_COOKIE_NAMES) {
        const valueBeforeRemovingSharedCookie = getCookie(name);
        const canonicalName = getCanonicalCookieName(name);
        const canonicalValue = getCookie(canonicalName);

        removeCookie(name, { path: "/", domain: cookieDomain });

        const hostOnlyValue = getCookie(name);
        const value = hostOnlyValue || canonicalValue || valueBeforeRemovingSharedCookie;

        removeCookie(canonicalName, { path: "/" });
        removeCookie(canonicalName, { path: "/", domain: cookieDomain });

        if (hostOnlyValue !== undefined && hostOnlyValue !== null && hostOnlyValue !== "") {
            removeCookie(name, { path: "/" });
        }

        if (value !== undefined && value !== null && value !== "") {
            setCookie(name, value, { path: "/", domain: cookieDomain, ...options });
            setCookie(canonicalName, value, { path: "/", domain: cookieDomain, ...options });
        }
    }

    watchForSharedContextChange();
}
